// ---------------------------------------------------------------------------
// EvaluationTemplateForm — pure helpers.
//
// Everything in here is React-free. Lifted out of EvaluationTemplateForm.tsx
// so the component file stays focused on rendering + state wiring.
// ---------------------------------------------------------------------------

import {
  DATA_LAKE_SOURCE_TYPE,
  DEFAULT_FILE_SELECTOR_NAME,
  TASK_NAME_PATTERN,
} from './EvaluationTemplateForm.types.js';
import type {
  AgentDraft,
  DataLakeFileSelector,
  DataSourceMode,
  ParallelismChoice,
  TaskDraft,
  VariableRow,
  VariableType,
} from './EvaluationTemplateForm.types.js';

// Hardcoded names for the form's single query/data set. Tasks always
// reference these names; the actual data lives on the named-set entries.
// Both keep the create payload and the edit-mode PATCH payload aligned.
export const QUERY_SET_NAME = 'default-query-set';
export const DATA_SET_NAME = 'default-data-set';

// ── Data Lake file-selector helpers ────────────────────────────────────────

// The lower-cased extension of a file name, without the dot (e.g.
// `meeting.TXT` -> `txt`), or null when the name has no usable extension.
export function fileExtension(fileName: string | undefined): string | null {
  if (fileName === undefined) return null;
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0 || dot === fileName.length - 1) return null;
  return fileName.slice(dot + 1).toLowerCase();
}

// Distinct, sorted extensions across a data item's attached files. Drives
// the Existing-mode format picker: one entry -> auto-select; many -> choose.
export function deriveFileExtensions(
  dataFiles: { fileName?: string }[] | undefined,
): string[] {
  const exts = new Set<string>();
  for (const f of dataFiles ?? []) {
    const ext = fileExtension(f.fileName);
    if (ext !== null) exts.add(ext);
  }
  return [...exts].sort();
}

// Maps the semantic `fileType` DataLake attaches to each file
// (transcript/audioStream/videoStream/generic) onto the selector's binding
// `name`. Only the name comes from `fileType`; the decode format (`as`) is
// left to the backend to infer from the extension (a transcript can be a
// `.txt` or a `.json`, so `fileType` alone can't decide it). Unknown/generic
// types fall back to the `transcript` alias.
export function fileSelectorNameForFileType(
  fileType: string | undefined,
): string {
  if (fileType === 'audioStream') return 'audio';
  if (fileType === 'videoStream') return 'video';
  return DEFAULT_FILE_SELECTOR_NAME;
}

// The shape of a Data Lake dataset's files, sampled from a page of data
// items. Drives the Existing-mode selector auto-config: the supported case is
// one file per item and a single extension across the sample.
export interface DataSetFileShape {
  // Distinct, sorted file extensions across the sampled items.
  extensions: string[];
  // The `fileType` of the first file seen — used to name the selector.
  fileType?: string;
  // The largest file count on any single sampled item. A value > 1 means
  // items carry multiple files, which the single-selector auto-config can't
  // express (out of scope for now).
  maxFilesPerItem: number;
  // The smallest file count on any single sampled item. A value of 0 means at
  // least one item has no file: a `*.ext` selector would match nothing for
  // that item and the backend would hard-fail the run, so this must also be 1
  // for the shape to be auto-configurable.
  minFilesPerItem: number;
}

// Inspects a sample of data items to decide whether the dataset is the
// supported "single file per item, single format" shape. Aggregating across
// several items (not just the first) lets us catch cross-item format mixing
// and items that are missing a file entirely.
export function analyzeDataSetFiles(
  items: { dataFiles?: { fileName?: string; fileType?: string }[] }[],
): DataSetFileShape {
  const extensions = new Set<string>();
  let fileType: string | undefined;
  let maxFilesPerItem = 0;
  let minFilesPerItem = items.length === 0 ? 0 : Number.POSITIVE_INFINITY;
  for (const item of items) {
    const files = item.dataFiles ?? [];
    maxFilesPerItem = Math.max(maxFilesPerItem, files.length);
    minFilesPerItem = Math.min(minFilesPerItem, files.length);
    for (const f of files) {
      const ext = fileExtension(f.fileName);
      if (ext !== null) extensions.add(ext);
      if (
        fileType === undefined &&
        f.fileType !== undefined &&
        f.fileType !== ''
      ) {
        fileType = f.fileType;
      }
    }
  }
  return {
    extensions: [...extensions].sort(),
    fileType,
    maxFilesPerItem,
    minFilesPerItem: Number.isFinite(minFilesPerItem) ? minFilesPerItem : 0,
  };
}

// Builds the default single-file selector for a detected extension. `as` is
// intentionally omitted so the backend infers the decode mode from the
// extension (txt -> text, json -> json, wav/mp3/etc -> base64). The binding
// `name` defaults to `transcript` but callers pass a `fileType`-derived name
// (e.g. `audio`) so prompts read the file as `$.dataItem.data.<name>.content`.
export function buildDefaultFileSelector(
  ext: string,
  name: string = DEFAULT_FILE_SELECTOR_NAME,
): DataLakeFileSelector {
  return { name, firstMatching: `*.${ext}` };
}

// Recovers the extension a `*.ext` glob targets, so a saved selector can be
// matched back to a format-picker option. Returns null for non-`*.ext` globs
// (e.g. `*` or `transcript.json`), which the picker treats as a custom
// selector it must not overwrite.
export function extensionFromGlob(glob: string): string | null {
  const match = /^\*\.([A-Za-z0-9]+)$/.exec(glob);
  return match?.[1]?.toLowerCase() ?? null;
}

// The binding path a prompt uses to read the selector's file content.
export function fileSelectorBindingPath(
  selector: DataLakeFileSelector,
): string {
  return `$.dataItem.data.${selector.name}.content`;
}

// ── Unique id helper ───────────────────────────────────────────────────────
// Used to key React list rows for agents/tasks/grader rows/variables. Format
// `<prefix>-<timestamp>-<counter>` so ids are unique within a session and
// somewhat readable in DevTools.
let __idCounter = 0;
export function uid(prefix: string): string {
  __idCounter += 1;
  return `${prefix}-${String(Date.now())}-${String(__idCounter)}`;
}

// ── Form-state factories ───────────────────────────────────────────────────

export function makeAgent(): AgentDraft {
  return {
    id: uid('a'),
    name: '',
  };
}

export function makeTask(name: string): TaskDraft {
  return {
    kind: 'single',
    id: uid('t'),
    name,
    agent: makeAgent(),
    graderRows: [],
  };
}

// ── Inline / file items parsing ────────────────────────────────────────────
// Inline data + query sets accept either a JSON array or a JSONL/text blob
// (one row per line). `parseInlineItems` returns `null` on unusable input so
// callers can surface a validation error.

export type InlineItemsKind =
  | 'empty'
  | 'jsonl'
  | 'json-array-strings'
  | 'json-array-objects'
  | 'single-json-document'
  | 'invalid';

export interface InlineItemsAnalysis {
  items: string[] | null;
  count: number;
  invalidCount: number;
  kind: InlineItemsKind;
  error?: string;
  warning?: string;
}

const JSON_ARRAY_ERROR =
  'This looks like a JSON array, but it could not be parsed. Provide a valid JSON array or JSONL (one JSON object per line).';

const SINGLE_JSON_DOCUMENT_ERROR =
  'This looks like a single JSON document, not a list of rows. Provide JSONL (one JSON object per line) or a JSON array.';

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function isStringArray(value: unknown[]): value is string[] {
  return value.every((item) => typeof item === 'string');
}

function stringifyInlineItem(value: unknown): string {
  if (typeof value === 'string') return value;
  const serialized = JSON.stringify(value);
  return serialized ?? String(value);
}

function tryParseJson(text: string): { value: unknown } | null {
  try {
    return { value: JSON.parse(text) as unknown };
  } catch {
    return null;
  }
}

function findSingleArrayProperty(value: unknown): {
  key: string;
  count: number;
} | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length !== 1) return null;
  const [key, child] = entries[0]!;
  return Array.isArray(child) ? { key, count: child.length } : null;
}

function singleJsonDocumentError(value: unknown): string {
  const arrayProperty = findSingleArrayProperty(value);
  if (arrayProperty === null) return SINGLE_JSON_DOCUMENT_ERROR;
  return `${SINGLE_JSON_DOCUMENT_ERROR} Did you mean the ${String(
    arrayProperty.count,
  )} ${pluralize(arrayProperty.count, 'item', 'items')} under "${
    arrayProperty.key
  }"?`;
}

function jsonlWarning(invalidCount: number): string | undefined {
  if (invalidCount === 0) return undefined;
  return `${String(invalidCount)} ${pluralize(
    invalidCount,
    'item is',
    'items are',
  )} not valid JSON and will be sent as raw text.`;
}

export function analyzeInlineItems(text: string): InlineItemsAnalysis {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { items: null, count: 0, invalidCount: 0, kind: 'empty' };
  }

  if (trimmed.startsWith('[')) {
    const parsed = tryParseJson(trimmed);
    if (parsed === null || !Array.isArray(parsed.value)) {
      return {
        items: null,
        count: 0,
        invalidCount: 0,
        kind: 'invalid',
        error: JSON_ARRAY_ERROR,
      };
    }

    if (isStringArray(parsed.value)) {
      return {
        items: parsed.value,
        count: parsed.value.length,
        invalidCount: 0,
        kind: 'json-array-strings',
      };
    }

    const items = parsed.value.map(stringifyInlineItem);
    return {
      items,
      count: items.length,
      invalidCount: 0,
      kind: 'json-array-objects',
    };
  }

  if (trimmed.startsWith('{')) {
    const parsed = tryParseJson(trimmed);
    // Only a *multi-line* JSON object is treated as a pasted document (the
    // "pretty-printed file" case). A single-line object like {"id":1} is a
    // valid one-row JSONL input and must fall through to the JSONL branch.
    const hasNewline = /[\r\n]/.test(trimmed);
    if (parsed !== null && hasNewline) {
      return {
        items: null,
        count: 0,
        invalidCount: 0,
        kind: 'single-json-document',
        error: singleJsonDocumentError(parsed.value),
      };
    }
  }

  const lines = trimmed
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return { items: null, count: 0, invalidCount: 0, kind: 'empty' };
  }

  const invalidCount = lines.filter(
    (line) => tryParseJson(line) === null,
  ).length;
  const warning = jsonlWarning(invalidCount);
  return {
    items: lines,
    count: lines.length,
    invalidCount,
    kind: 'jsonl',
    ...(warning !== undefined ? { warning } : {}),
  };
}

export function parseInlineItems(text: string): string[] | null {
  return analyzeInlineItems(text).items;
}

export async function readFileAsInlineItemsAnalysis(
  file: File,
): Promise<InlineItemsAnalysis> {
  const text = await file.text();
  return analyzeInlineItems(text);
}

export async function readFileAsItems(file: File): Promise<string[]> {
  const analysis = await readFileAsInlineItemsAnalysis(file);
  if (analysis.items === null) {
    throw new Error(
      `Couldn't parse '${file.name}'. ${
        analysis.error ??
        'Provide a JSON array or JSONL (one JSON object per line).'
      }`,
    );
  }
  return analysis.items;
}

// ── Variable default parsing/formatting ────────────────────────────────────

// User-typed default → typed value. Returns `undefined` for empty input
// (treated as "no default"). Boolean defaults are 'true' / anything-else,
// matching the rest of the form's loose typing.
export function parseDefault(value: string, type: VariableType): unknown {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  if (type === 'boolean') return trimmed === 'true';
  if (type === 'number' || type === 'integer') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : undefined;
  }
  return trimmed;
}

// Stringifies a variable's persisted default for the editable text input.
// Objects/arrays come back as JSON so the user sees something meaningful
// in the field — Object's default `toString` would just write
// `[object Object]`.
export function formatVariableDefault(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

// ── Validation predicates ──────────────────────────────────────────────────

export function isValidTaskName(name: string): boolean {
  return name.length > 0 && TASK_NAME_PATTERN.test(name);
}

// ── Template body builder ──────────────────────────────────────────────────
// Single source of truth for the shape sent to PUT (create) and PATCH
// (edit). Both call sites used to inline this assembly, drifting in subtle
// ways. Pure function — takes the form state, returns the body subset
// covering everything except identity metadata (displayName/description),
// which each call site adds based on its own change-detection rules.

export interface BuildTemplateBodyParams {
  taskDrafts: TaskDraft[];
  variables: VariableRow[];
  querySetItems: string[] | null;
  dataSetEnabled: boolean;
  dataSetMode: DataSourceMode;
  dataSetItems: string[] | null;
  dataSetExistingId: string | null;
  dataSetExistingVersion: number | null;
  dataSetFileSelector: DataLakeFileSelector[];
  workersText: string;
  parallelism: ParallelismChoice;
}

export interface TemplateBodyParts {
  querySets: unknown[];
  dataSets?: unknown[];
  tasks: unknown[];
  /** Always present; caller may omit when empty for the create path. */
  variables: Record<string, unknown>;
  concurrency: unknown;
}

export function buildTemplateBody(
  params: BuildTemplateBodyParams,
): TemplateBodyParts {
  // Variables: { NAME: { type, default?, description? } }
  const variables = params.variables
    .filter((v) => v.name.length > 0)
    .reduce<
      Record<
        string,
        { type: VariableType; default?: unknown; description?: string }
      >
    >((acc, v) => {
      const decl: {
        type: VariableType;
        default?: unknown;
        description?: string;
      } = { type: v.type };
      const def = parseDefault(v.defaultValue, v.type);
      if (def !== undefined) decl.default = def;
      if (v.description.trim().length > 0) {
        decl.description = v.description.trim();
      }
      acc[v.name] = decl;
      return acc;
    }, {});

  // Concurrency: parse workers ('auto' / empty → omit so the server picks).
  const workersTrimmed = params.workersText.trim();
  const parsedWorkers =
    workersTrimmed.length === 0 || workersTrimmed.toLowerCase() === 'auto'
      ? undefined
      : Number(workersTrimmed);

  // Tasks: map TaskDraft → server-shape task. Single-run only — one task
  // with one `agent` reference plus its grader references.
  const tasks = params.taskDrafts.map((t) => {
    const graders = Array.from(
      new Map(
        t.graderRows
          .filter((row) => row.name.length > 0)
          .map((row) => [row.name, row]),
      ).values(),
    );
    return {
      name: t.name,
      querySet: QUERY_SET_NAME,
      ...(params.dataSetEnabled ? { dataSet: DATA_SET_NAME } : {}),
      ...(graders.length > 0
        ? {
            graders: graders.map((grader) => ({
              name: grader.name,
              ...(grader.version === undefined
                ? {}
                : { version: grader.version }),
            })),
          }
        : {}),
      agent: {
        name: t.agent.name,
        ...(t.agent.version === undefined ? {} : { version: t.agent.version }),
      },
    };
  });

  // Data set: inline `items` (Inline/File modes) or `source` reference
  // (Existing mode). Server fans these out via the polymorphic
  // EvaluationTemplateDataSourceRef discriminator. The mode — not the
  // presence of an id — picks the shape, so an Existing-mode set with no id
  // yet emits a (server-rejectable) `source` rather than silently degrading
  // to an empty inline set. (Submit validation blocks that state anyway.)
  const dataSets = params.dataSetEnabled
    ? [
        params.dataSetMode === 'existing'
          ? {
              name: DATA_SET_NAME,
              source: {
                sourceType: DATA_LAKE_SOURCE_TYPE,
                ...(params.dataSetExistingId !== null
                  ? { dataSetId: params.dataSetExistingId }
                  : {}),
                ...(params.dataSetExistingVersion !== null
                  ? { version: params.dataSetExistingVersion }
                  : {}),
                // Backend requires a non-empty fileSelector for Data Lake
                // sources. Each entry drops `as` when unset so the harness
                // infers the decode mode from the file extension.
                fileSelector: params.dataSetFileSelector.map((s) => ({
                  name: s.name,
                  firstMatching: s.firstMatching,
                  ...(s.as !== undefined ? { as: s.as } : {}),
                })),
              },
            }
          : { name: DATA_SET_NAME, items: params.dataSetItems ?? [] },
      ]
    : undefined;

  return {
    querySets: [{ name: QUERY_SET_NAME, items: params.querySetItems ?? [] }],
    ...(dataSets !== undefined ? { dataSets } : {}),
    tasks,
    variables,
    concurrency: {
      parallelism: params.parallelism,
      ...(parsedWorkers !== undefined && Number.isFinite(parsedWorkers)
        ? { workers: parsedWorkers }
        : {}),
    },
  };
}
